import React, { useState, useMemo } from 'react';
import { USER_TIERS } from '../../../constants/tiers';
import CosmicIcon from '../../common/CosmicIcon';
import { getElementDescription } from '../../../data/elementDescriptions';
import './CosmicDomainStrengths.css';

/**
 * Cosmic Domain Strengths - Enhanced domain display with cosmic styling
 * Shows all 5 domains with scores and tier-based element access
 * For Premium users: Shows ALL elements per domain from allElements
 * For lower tiers: Shows limited elements from topPreferences
 */
const CosmicDomainStrengths = ({ results, tier, domainColors }) => {
  const [selectedElement, setSelectedElement] = useState(null);

  // Structure's brand purple (#54548E) is too dark to read on the navy surface:
  // its strength bars and modal accents disappear next to the other domains.
  // Use a lightened purple for Structure specifically so every JS-inline fill
  // matches the lightened --domain-color CSS var set on the column below. We
  // deliberately do NOT touch the global token / DOMAIN_COLORS (used elsewhere).
  const getDisplayColor = (domainId) =>
    domainId === 'structure' ? '#8B8BC4' : domainColors[domainId];
  
  // Get all domains with scores - sorted by score (memoized)
  const domainData = useMemo(() => {
    if (!results?.domains) return [];
    return Object.entries(results.domains)
      .map(([domainId, score]) => ({ id: domainId, score }))
      .sort((a, b) => b.score - a.score);
  }, [results?.domains]);
  
  // Get the first domain ID for initial expansion
  const firstDomainId = domainData.length > 0 ? domainData[0].id : null;
  
  // Track expanded domains for mobile accordion - default to first domain expanded
  const [expandedDomains, setExpandedDomains] = useState(() => {
    // Open the user's actual top-scored domain on load (was hardcoded to
    // 'insight', so a taker whose top domain was e.g. Impact saw a mid-ranked
    // domain expanded and their #1 collapsed).
    return new Set(firstDomainId ? [firstDomainId] : ['insight']);
  });
  
  // Element descriptors mapping (elementId -> single-word descriptor) - 45 elements per preferences.js
  const elementDescriptors = {
    // Insight Domain (9 elements)
    analysis: 'Analyst', research: 'Investigator', dataInterpretation: 'Interpreter',
    insightExploration: 'Explorer', intellectualChallenge: 'Solver', environmentalAwareness: 'Observer',
    knowledgeCuration: 'Curator', comparativeAnalysis: 'Evaluator', informationSynthesis: 'Integrator',
    // Vision Domain (9 elements)
    creativity: 'Imagineer', strategicDirection: 'Strategist', innovation: 'Innovator',
    brainstorming: 'Generator', visionDevelopment: 'Visionary', experienceDesign: 'Designer',
    problemFraming: 'Framer', aestheticSensibility: 'Aesthete', longTermPerspective: 'Foreseer',
    // Connection Domain (9 elements)
    teamCollaboration: 'Collaborator', relationshipBuilding: 'Connector', stakeholderNavigation: 'Diplomat',
    trustDevelopment: 'Trustbuilder', emotionalSupport: 'Nurturer', culturalIntelligence: 'Bridger',
    relationalListening: 'Listener', consensusBuilding: 'Harmonizer', bridgingPerspectives: 'Translator',
    // Impact Domain (9 elements)
    influence: 'Influencer', persuasiveCommunication: 'Persuader', publicEngagement: 'Presenter',
    teaching: 'Educator', service: 'Contributor', knowledgeDistribution: 'Disseminator',
    valuesContribution: 'Advocate', coaching: 'Coach', ethicalLeadership: 'Steward',
    // Structure Domain (9 elements)
    systemsDesign: 'Architect', structuredExecution: 'Implementer', operationsManagement: 'Operator',
    processImprovement: 'Optimizer', organizationalDesign: 'Organizer', efficiencyTuning: 'Streamliner',
    toolDevelopment: 'Toolsmith', resourceAlignment: 'Allocator', sustainablePlanning: 'Sustainer'
  };
  
  // Get all 9 elements for a domain - uses allElements when available
  const getDomainElements = (domainId) => {
    // List of all element IDs per domain (from preferences.js structure)
    const domainElementIds = {
      insight: ['analysis', 'research', 'dataInterpretation', 'insightExploration', 'intellectualChallenge', 'environmentalAwareness', 'knowledgeCuration', 'comparativeAnalysis', 'informationSynthesis'],
      vision: ['creativity', 'strategicDirection', 'innovation', 'brainstorming', 'visionDevelopment', 'experienceDesign', 'problemFraming', 'aestheticSensibility', 'longTermPerspective'],
      connection: ['teamCollaboration', 'relationshipBuilding', 'stakeholderNavigation', 'trustDevelopment', 'emotionalSupport', 'culturalIntelligence', 'relationalListening', 'consensusBuilding', 'bridgingPerspectives'],
      impact: ['influence', 'persuasiveCommunication', 'publicEngagement', 'teaching', 'service', 'knowledgeDistribution', 'valuesContribution', 'coaching', 'ethicalLeadership'],
      structure: ['systemsDesign', 'structuredExecution', 'operationsManagement', 'processImprovement', 'organizationalDesign', 'efficiencyTuning', 'toolDevelopment', 'resourceAlignment', 'sustainablePlanning']
    };
    
    const elementIds = domainElementIds[domainId] || [];
    
    // If we have allElements data, use it to get scores
    if (results.allElements) {
      const elementsWithScores = results.allElements
        .filter(el => el.domain === domainId)
        .reduce((acc, el) => {
          acc[el.elementId] = el;
          return acc;
        }, {});
      
      return elementIds.map((elementId, idx) => {
        const element = elementsWithScores[elementId];
        if (element) {
          return {
            id: elementId,
            name: elementDescriptors[elementId] || elementId,
            descriptor: elementDescriptors[elementId] || elementId,
            score: element.strengthPercentile || Math.round(((element.theta + 3) / 6) * 100),
            theta: element.theta,
            reliability: element.reliability,
            globalRank: element.rank || (idx + 1),
            domainId: domainId,
            hasScore: true
          };
        }
        // Element exists but no score data yet
        return {
          id: elementId,
          name: elementDescriptors[elementId] || elementId,
          descriptor: elementDescriptors[elementId] || elementId,
          score: null,
          globalRank: null,
          domainId: domainId,
          hasScore: false
        };
      });
    }
    
    // Fallback: use topPreferences and fill remaining with empty slots
    const preferencesMap = {};
    if (results.topPreferences) {
      results.topPreferences
        .filter(pref => pref.domainId === domainId)
        .forEach((pref, idx) => {
          preferencesMap[pref.elementId || pref.id] = {
            ...pref,
            score: pref.score > 1 ? pref.score : Math.round(pref.score * 100),
            globalRank: pref.rank || (idx + 1)
          };
        });
    }
    
    return elementIds.map((elementId, idx) => {
      if (preferencesMap[elementId]) {
        return {
          ...preferencesMap[elementId],
          id: elementId,
          name: elementDescriptors[elementId] || elementId,
          descriptor: elementDescriptors[elementId] || elementId,
          domainId: domainId,
          hasScore: true
        };
      }
      return {
        id: elementId,
        name: elementDescriptors[elementId] || elementId,
        descriptor: elementDescriptors[elementId] || elementId,
        score: null,
        globalRank: null,
        domainId: domainId,
        hasScore: false
      };
    });
  };
  
  // Get top 2 domain IDs for Free tier restrictions
  const topTwoDomainIds = domainData.slice(0, 2).map(d => d.id);
  
  // Pre-compute unlocked element IDs for Free and Enhanced tiers
  // Free: Top 3 scoring elements within top 2 domains
  // Enhanced: Top 8 scoring elements across all domains (full constellation)
  const unlockedElementIds = useMemo(() => {
    if (tier === USER_TIERS.PREMIUM) return null; // Premium sees all, no need for set
    
    // Gather all elements with scores
    const allElements = [];
    domainData.forEach((domain, domainIndex) => {
      const domainRank = domainIndex + 1;
      const elements = getDomainElements(domain.id);
      elements.forEach(el => {
        if (el.hasScore && el.score != null) {
          allElements.push({
            ...el,
            domainRank
          });
        }
      });
    });
    
    if (tier === USER_TIERS.ENHANCED) {
      // Enhanced: Top 8 scoring elements across ALL domains (full constellation)
      const top8 = allElements
        .sort((a, b) => b.score - a.score)
        .slice(0, 8)
        .map(el => el.id);
      return new Set(top8);
    }
    
    // Free tier: Top 3 scoring elements, but ONLY from top 2 domains
    const elementsInTop2Domains = allElements.filter(el => el.domainRank <= 2);
    const top3 = elementsInTop2Domains
      .sort((a, b) => b.score - a.score)
      .slice(0, 3)
      .map(el => el.id);
    return new Set(top3);
  }, [tier, domainData, results]);
  
  // Early return if no results
  if (!results || !results.domains) return null;
  
  // Toggle domain expansion for mobile accordion
  const toggleDomain = (domainId) => {
    setExpandedDomains(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(domainId)) {
        newExpanded.delete(domainId);
      } else {
        newExpanded.add(domainId);
      }
      return newExpanded;
    });
  };
  
  // Check if user can see DOMAIN SCORE (percentage) based on tier
  // All tiers see all 5 domain scores — element-level detail is tier-gated separately
  const canSeeDomainScore = (domainRank) => {
    return true; // All tiers see all 5 domain percentages
  };
  
  // Check if user can see element SCORE based on tier
  // Returns: true = show score, false = locked (but name still visible)
  const canSeeElementScore = (element) => {
    // If element has no score data yet, it's locked regardless of tier
    if (!element.hasScore || !element.globalRank) return false;
    
    if (tier === USER_TIERS.PREMIUM) return true; // Premium sees all
    
    // For Free and Enhanced, check if this element is in the unlocked set
    return unlockedElementIds.has(element.id);
  };
  
  return (
    <div className="cosmic-domain-strengths cosmic-domain-strengths--five-column">
      <div className="cosmic-card cosmic-card--professional cosmic-card--full-width">
        
        <div className="cosmic-card__header cosmic-card__header--enhanced">
          <div className="cosmic-header-content">
            <h2 className="cosmic-card__title"><CosmicIcon name="results_domains" size={24} decorative /> Your Complete Element Galaxy</h2>
            
            {/* Tier-specific explanation */}
            {tier === USER_TIERS.PREMIUM ? (
              <p className="cosmic-card__subtitle">
                All 45 elements across five cosmic domains
                <span className="cosmic-premium-badge"> • All Elements Unlocked <CosmicIcon name="brand_spark" size={14} decorative /></span>
              </p>
            ) : tier === USER_TIERS.ENHANCED ? (
              <div className="cosmic-tier-explanation">
                <p className="cosmic-card__subtitle">
                  You're viewing your <strong>top 8 motivated elements</strong> ranked globally across all 45 elements.
                </p>
                <p className="cosmic-tier-detail">
                  Global rankings (#1-#8) show how each element compares to all 45 possible motivators — not just within each domain.
                </p>
              </div>
            ) : (
              <div className="cosmic-tier-explanation">
                <p className="cosmic-card__subtitle">
                  You're viewing your <strong>top 3 motivated elements</strong> within your top 2 scoring domains.
                </p>
                <p className="cosmic-tier-detail">
                  These show <strong>global rankings</strong> across all 45 elements — so you might see #3, #7, #12 instead of #1, #2, #3.
                </p>
              </div>
            )}
          </div>
          
          {/* Upgrade CTA - only show for non-Premium tiers */}
          {tier !== USER_TIERS.PREMIUM && (
            <div className="cosmic-header-upgrade">
              <button 
                className="cosmic-cta cosmic-cta--sm"
                onClick={() => {
                  // Trigger upgrade modal or scroll to upgrade section
                  const upgradeSection = document.querySelector('.cosmic-upgrade-section');
                  if (upgradeSection) {
                    upgradeSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }
                }}
              >
                <span className="cosmic-cta-icon"><CosmicIcon name="ui_bolt" size={18} decorative /></span>
                {tier === USER_TIERS.FREE ? 'Unlock All 45 Elements' : 'Upgrade to Premium'}
                <span className="cosmic-upgrade-arrow">→</span>
              </button>
              <span className="cosmic-upgrade-hint-text">
                {tier === USER_TIERS.FREE 
                  ? 'See your complete career constellation' 
                  : 'Unlock deep insights across all domains'}
              </span>
            </div>
          )}
        </div>
        
        <div className="cosmic-card__body">
          
          {/* Domain Strength Grid - 5 columns on desktop, accordion on mobile */}
          {/* All tiers see all 5 domains, but Free tier only sees scores for top 2 */}
          <div className="cosmic-domains-grid cosmic-domains-grid--five-column">
            {domainData.map((domain, domainIndex) => {
              const domainRank = domainIndex + 1;
              const elements = getDomainElements(domain.id);
              const isExpanded = expandedDomains.has(domain.id);
              const showDomainScore = canSeeDomainScore(domainRank);
              
              return (
                <div 
                  key={domain.id}
                  className={`cosmic-domain-column ${tier === USER_TIERS.PREMIUM ? 'cosmic-domain-column--premium' : ''} ${isExpanded ? 'cosmic-domain-column--expanded' : ''} ${!showDomainScore ? 'cosmic-domain-column--score-locked' : ''}`}
                  style={{'--domain-color': getDisplayColor(domain.id)}}
                >
                  
                  {/* Domain Header - Clickable for accordion on mobile */}
                  <div 
                    className="cosmic-domain-column-header"
                    onClick={() => toggleDomain(domain.id)}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => e.key === 'Enter' && toggleDomain(domain.id)}
                    aria-expanded={isExpanded}
                  >
                    <div className="cosmic-domain-header-content">
                      <div className="cosmic-domain-icon">
                        <CosmicIcon name={`domain_${domain.id}`} size={36} decorative />
                      </div>
                      <div className="cosmic-domain-title-group">
                        <h3 className="cosmic-domain-name">
                          {getDomainName(domain.id)}
                        </h3>
                        {/* Domain marker - rank only (percentage numbers hidden by
                            product decision; strength still shown via the bar). */}
                        <div className={`cosmic-domain-score ${!showDomainScore ? 'cosmic-domain-score--locked' : ''}`}>
                          {showDomainScore ? `#${domainRank}` : <CosmicIcon name="ui_locked" size={16} decorative />}
                        </div>
                      </div>
                    </div>
                    {/* Accordion chevron - visible on mobile */}
                    <div className={`cosmic-domain-chevron ${isExpanded ? 'cosmic-domain-chevron--expanded' : ''}`}>
                      ▼
                    </div>
                  </div>
                  
                  {/* Score Bar - only show if domain score is visible */}
                  {showDomainScore ? (
                    <div className="cosmic-score-bar-mini">
                      <div className="cosmic-score-track">
                        <div 
                          className="cosmic-score-fill"
                          style={{width: `${domain.score}%`}}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="cosmic-score-bar-mini cosmic-score-bar-mini--locked">
                      <div className="cosmic-score-track cosmic-score-track--locked">
                        {/* Empty locked bar - no text needed */}
                      </div>
                    </div>
                  )}
                  
                  {/* Domain Elements - 9 elements sorted by global rank */}
                  <div className={`cosmic-elements-list ${isExpanded ? 'cosmic-elements-list--expanded' : ''}`}>
                    {[...elements]
                      .sort((a, b) => {
                        // Sort by global rank (lowest rank first), put unranked elements at end
                        if (!a.globalRank && !b.globalRank) return 0;
                        if (!a.globalRank) return 1;
                        if (!b.globalRank) return -1;
                        return a.globalRank - b.globalRank;
                      })
                      .map((element) => {
                      // Check if score is unlocked (based on tier and pre-computed unlocked set)
                      const isScoreUnlocked = canSeeElementScore(element);
                      // Check if this is a top 5 global element (only highlight if score is unlocked)
                      const isTopFive = isScoreUnlocked && element.globalRank && element.globalRank <= 5;
                      
                      return (
                        <div 
                          key={element.id}
                          className={`cosmic-element-item cosmic-element-item--orb cosmic-element-item--clickable ${!isScoreUnlocked ? 'cosmic-element-item--locked' : ''} ${isTopFive ? 'cosmic-element-item--top-five' : ''}`}
                          onClick={() => setSelectedElement({ 
                            ...element, 
                            domainId: domain.id, 
                            isScoreUnlocked,
                            domainRank 
                          })}
                          role="button"
                          tabIndex={0}
                          aria-label={isScoreUnlocked && element.globalRank
                            ? `${element.descriptor || element.name}, ranked number ${element.globalRank} of 45`
                            : `${element.descriptor || element.name}, locked`}
                          onKeyPress={(e) => e.key === 'Enter' && setSelectedElement({
                            ...element,
                            domainId: domain.id,
                            isScoreUnlocked,
                            domainRank
                          })}
                        >
                          {/* Rank medallion: leads with the global rank. The old
                              score-ring arc was removed because it over-communicated
                              score; domain color is now a calm accent, not a meter.
                              Top-five get the gold medallion + a corner star; locked
                              show a lock glyph and no rank. */}
                          <div className="cosmic-element-medal">
                            {isScoreUnlocked && element.globalRank ? (
                              <span className="cosmic-element-medal__rank">
                                <span className="cosmic-element-medal__hash">#</span>{element.globalRank}
                              </span>
                            ) : (
                              <CosmicIcon name="ui_locked" size={16} decorative />
                            )}
                            {isTopFive && <span className="cosmic-element-medal__star" aria-hidden="true">★</span>}
                          </div>
                          <div className={`cosmic-element-name ${isTopFive ? 'cosmic-element-name--top-five' : ''} ${!isScoreUnlocked ? 'cosmic-element-name--locked' : ''}`}>
                            {element.descriptor || element.name || element.id}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                </div>
              );
            })}
          </div>
          
        </div>
      </div>
      
      {/* Element Detail Modal - Shows description for all, score only if unlocked */}
      {selectedElement && (
        <ElementDetailModal
          element={selectedElement}
          domainColor={getDisplayColor(selectedElement.domainId)}
          tier={tier}
          isScoreUnlocked={selectedElement.isScoreUnlocked}
          onClose={() => setSelectedElement(null)}
        />
      )}
    </div>
  );
};

/**
 * Element Detail Modal - Shows detailed info when clicking an element
 * All users can see element name and description
 * Score only visible if isScoreUnlocked is true
 */
const ElementDetailModal = ({ element, domainColor, tier, isScoreUnlocked, onClose }) => {
  const nextTierName = tier === USER_TIERS.FREE ? 'Enhanced' : 'Premium';
  
  return (
    <div className="cosmic-element-modal-overlay" onClick={onClose}>
      <div 
        className={`cosmic-element-detail-modal ${!isScoreUnlocked ? 'cosmic-element-detail-modal--locked' : ''}`}
        onClick={e => e.stopPropagation()}
        style={{ '--domain-color': domainColor }}
      >
        {/* Close Button */}
        <button className="cosmic-element-modal-close" onClick={onClose}>✕</button>
        
        {/* Modal Header with Domain Badge */}
        <div className="cosmic-element-modal-header">
          <div className="cosmic-element-domain-badge">
            <CosmicIcon name={`domain_${element.domainId}`} size={16} decorative /> {getDomainName(element.domainId)}
          </div>
          {/* Rank is shown once, in the medallion below — no duplicate corner badge. */}
        </div>
        
        {/* Element Name - Large and Prominent (always visible) */}
        <h2 className="cosmic-element-modal-name">
          {element.descriptor || element.name || element.id}
        </h2>
        
        {/* Rank Section - leads with the global rank as a polished medallion.
            The old score-magnitude ring was removed (it over-communicated score);
            we surface rank only, consistent with the galaxy cards. */}
        {isScoreUnlocked && element.globalRank ? (
          <div className="cosmic-element-score-section">
            <div className={`cosmic-rank-medal-lg ${element.globalRank <= 5 ? 'cosmic-rank-medal-lg--gold' : ''}`}>
              <span className="cosmic-rank-medal-lg__rank">
                <span className="cosmic-rank-medal-lg__hash">#</span>{element.globalRank}
              </span>
            </div>
            <div className="cosmic-element-score-label">Your strength rank</div>
            {tier === USER_TIERS.FREE && (
              <p style={{
                color: 'rgba(255,255,255,0.55)',
                fontSize: '12px',
                margin: '6px 0 0',
                lineHeight: '1.4',
                textAlign: 'center',
                padding: '0 12px'
              }}>
                Upgrade to Enhanced to unlock more of your ranked elements
              </p>
            )}
          </div>
        ) : !isScoreUnlocked ? (
          /* Locked Score Section with Upgrade CTA */
          <div className="cosmic-element-locked-section">
            <div className="cosmic-locked-score-ring">
              <div className="cosmic-locked-icon"><CosmicIcon name="ui_locked" size={36} decorative /></div>
            </div>
            <div className="cosmic-locked-message">
              Your score for this element is locked
            </div>
            <button className="cosmic-cta cosmic-cta--sm">
              <CosmicIcon name="ui_bolt" size={16} decorative /> Upgrade to {nextTierName} to Unlock
            </button>
          </div>
        ) : null}
        
        {/* Element Description */}
        <div className="cosmic-element-modal-description">
          <p>
            {getElementDescription(element.id, element.domainId)}
          </p>
        </div>
        
      </div>
    </div>
  );
};

// getElementDescription now lives in src/data/elementDescriptions.js (imported above),
// shared with the constellation / star modals in CosmicConstellationTab so both
// surfaces render the same rich copy.

// Helper functions
const getDomainName = (domainId) => {
  const domainNames = {
    insight: 'Insight',
    vision: 'Vision',
    connection: 'Connection', 
    impact: 'Impact',
    structure: 'Structure'
  };
  return domainNames[domainId] || domainId;
};

export default CosmicDomainStrengths;
